// The virtual framebuffer.
//
// This is the only mutable state in what is otherwise a pile of constants, and
// it lived in `src/sim/constants.ts` until it was noticed that the simulation
// and the Worker both import that file. A module-level `let` in a Worker is
// shared by every request the isolate handles, so a value that changes with the
// size of somebody's browser window had no business being reachable from the
// server at all — and `constants.ts` is the one file whose whole claim is that
// it is data the client, the server and the tests can all agree on.
//
// Nothing outside the browser reads any of this, so it lives here.

/** The bounds the logical size is clamped into, and the shape it starts at. */
export const VIEW_MIN_W = 288;
export const VIEW_MAX_W = 416;
export const VIEW_MIN_H = 176;
export const VIEW_MAX_H = 288;
export const DESIGN_W = 384;
export const DESIGN_H = 240;

/** The window's aspect ratio picks these at boot and on every resize. They are
 *  live bindings: importers see the current value, not the value at import. */
export let VIEW_W = DESIGN_W;
export let VIEW_H = DESIGN_H;

const clampView = (v: number, lo: number, hi: number): number => (v < lo ? lo : Math.min(v, hi));

/** Set the logical framebuffer size. Returns the values actually adopted. */
export function setViewSize(w: number, h: number): { w: number; h: number } {
  VIEW_W = clampView(Math.round(w), VIEW_MIN_W, VIEW_MAX_W);
  VIEW_H = clampView(Math.round(h), VIEW_MIN_H, VIEW_MAX_H);
  return { w: VIEW_W, h: VIEW_H };
}
